import useSharedClientForm, {
  ApiFieldName,
} from "@shared/features/onboarding/hooks/useSharedClientForm";
import { getAnalyticsBotSource, openMessage } from "utils/misc";
import { useWhitelistIPs } from "features/configuration/accounts/CreateAccount/WhitelistIPs";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { useContext, useEffect, useMemo } from "react";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { IS_WHOP, MOBILE_SEARCH } from "common/constants";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import { OnboardingStep } from "analytics/events";
import { ClientFormProps } from "features/configuration/common/types";
import useConfirmModal from "common/Modal/useConfirmModal";
import { getDisplayResultMessageFunction } from "@shared/utils/misc";
import { useOnboardingAnalytics } from "analytics/onboarding";
import {
  getConnectContext,
  toErrorMessage,
  useAccountSetupAnalytics,
} from "analytics/accountSetup";

export type InlineErrorHandler = (description: string, title?: string) => void;

interface UseClientFormProps extends ClientFormProps {
  onInlineError?: InlineErrorHandler;
}

const useClientForm = ({
  account,
  onSubmitSuccess,
  closeDrawer,
  form,
  isOnboarding,
  getErrorNotification,
  primaryExchange,
  getSuccessMessage,
  onInlineError,
}: UseClientFormProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const trackOnboarding = useOnboardingAnalytics();
  const trackAccountSetup = useAccountSetupAnalytics();
  const connectProperties = {
    context: getConnectContext(isOnboarding),
    exchange: primaryExchange,
    connect_method: "api_keys" as const,
  };
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { confirmModalContextHolder, openConfirmModal } = useConfirmModal({
    modalWidth: 600,
  });
  const { tryAgainLoading, quickConnectLoading } =
    useContext(QuickConnectContext) || {};

  useEffect(() => {
    analytics?.createAccountView({
      botSource: getAnalyticsBotSource(),
    });
    trackAccountSetup("Connect Account - Viewed", {
      context: getConnectContext(isOnboarding),
      exchange: primaryExchange,
    });
  }, [analytics, trackAccountSetup]);

  useEffect(() => {
    if (tryAgainLoading && primaryExchange)
      analytics?.connectExchangeTryAgainClicked({
        onboardingStep: isOnboarding
          ? OnboardingStep.CONNECT_EXCHANGE
          : undefined,
        clientExchange: primaryExchange,
      });
  }, [analytics, tryAgainLoading, primaryExchange]);

  const onConnectToExchangeClicked = () => {
    trackAccountSetup("Connect Account - Connect Clicked", connectProperties);
    if (isOnboarding && primaryExchange) {
      analytics?.onboardingConnectToExchangeClicked({
        onboardingStep: OnboardingStep.CONNECT_EXCHANGE,
        clientExchange: primaryExchange,
        createClientIsQuickConnect: false,
      });
    }
  };

  // Onboarding: whether the keys connected, or why not, ends the step.
  const trackConnectFailure = (message?: string) => {
    trackAccountSetup("Connect Account - Failed", {
      ...connectProperties,
      error_message: toErrorMessage(message),
    });
    if (isOnboarding)
      trackOnboarding("Step Failed", {
        step: "connect_exchange",
        error_code: "exchange_connection_failed",
        exchange: primaryExchange,
        connect_method: "api_keys",
      });
  };
  const submitSuccess = () => {
    trackAccountSetup("Connect Account - Connected", connectProperties);
    if (isOnboarding)
      trackOnboarding("Exchange Connected", {
        exchange: primaryExchange,
        connect_method: "api_keys",
      });
    onSubmitSuccess?.();
  };
  const errorNotification = (description: string, title?: string) => {
    trackConnectFailure(description);
    (onInlineError ?? getErrorNotification)?.(description, title);
  };
  // Every failed connection is recorded; inline errors replace the toast.
  const resultMessage = useMemo(
    () =>
      getDisplayResultMessageFunction((text, type, duration) => {
        if (type === "error") trackConnectFailure(text);
        return type === "error" && onInlineError
          ? onInlineError(text)
          : openMessage(text, type, duration);
      }),
    [
      onInlineError,
      isOnboarding,
      primaryExchange,
      trackOnboarding,
      trackAccountSetup,
    ]
  );

  const {
    isFetchingWhitelistIPs,
    isQuickConnectExchange,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    validateApiInfo: sharedValidateApiInfo,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    exchangesLoading,
    accountSubExchange,
    isHyperLiquid,
  } = useSharedClientForm({
    account,
    primaryExchange,
    onSubmitSuccess: submitSuccess,
    closeDrawer,
    displayResultMessage: resultMessage,
    useWhitelistIPs,
    onConnectToExchangeClicked,
    getErrorNotification:
      onInlineError || getErrorNotification ? errorNotification : undefined,
    useQuotaModal,
    openConfirmModal,
    getSuccessMessage,
    isOnboarding,
    isWhop: IS_WHOP,
  });

  const validateApiInfo = (fieldName: ApiFieldName, fieldLabel: string) => {
    const errorMessage = sharedValidateApiInfo(
      form.getFieldsValue(),
      fieldName,
      fieldLabel
    );
    if (errorMessage) {
      return Promise.reject(new Error(errorMessage));
    }
    return Promise.resolve();
  };

  const isLoading = exchangesLoading || isFetchingWhitelistIPs;

  return {
    appTheme,
    isMobile,
    isQuickConnectExchange,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    validateApiInfo,
    isLoading,
    quickConnectLoading,
    openConfirmModal,
    confirmModalContextHolder,
    accountSubExchange,
    isHyperLiquid,
  };
};

export default useClientForm;
